import { FC, ReactElement, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { Alert, Button, Col, Form, InputGroup, Row, Spinner, Stack } from 'react-bootstrap';
import { TelerehabilitationProgramDto } from '../../../../../shared/dtos/telerehabilitationProgram.dto';
import { TelerehabilitationProgramRequest } from '../../../../../shared/requests/telerehabilitationProgram.request';
import { useAuthFetchFromRestAPI } from '../../../util/api';

type FormValues = {
  germanName: string;
  englishName: string;
  targetTrainingMinutes: number;
  teConversion: number;
  basicPackage: number;
  maximumBillableTherapyUnits: number;
  maximumBillableTeleTherapyUnits: number;
  maximumEnduranceTrainingPercentage: number;
}

type TelerehabilitationProgramFormProps = {
  data: TelerehabilitationProgramDto | null;
  onClose: () => void;
  onSubmit: () => void;
}

const TelerehabilitationProgramForm: FC<TelerehabilitationProgramFormProps> = ({
  data,
  onClose,
  onSubmit
}: TelerehabilitationProgramFormProps): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = {
    germanName: data?.germanName ?? '',
    englishName: data?.englishName ?? '',
    targetTrainingMinutes: data?.targetTrainingMinutes ?? 0,
    teConversion: data?.teConversion ?? 0,
    basicPackage: data?.basicPackage ?? 0,
    maximumBillableTherapyUnits: data?.maximumBillableTherapyUnits ?? 0,
    maximumBillableTeleTherapyUnits: data?.maximumBillableTeleTherapyUnits ?? 0,
    maximumEnduranceTrainingPercentage: data?.maximumEnduranceTrainingPercentage ?? 0,
  };
  
  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    germanName: Yup.string().required(i18next.t('generic.required')),
    englishName: Yup.string().required(i18next.t('generic.required')),
    targetTrainingMinutes: Yup.number().integer().required(i18next.t('generic.required')),
    teConversion: Yup.number().integer().required(i18next.t('generic.required')),
    basicPackage: Yup.number().integer().required(i18next.t('generic.required')),
    maximumBillableTherapyUnits: Yup.number().integer().required(i18next.t('generic.required')),
    maximumBillableTeleTherapyUnits: Yup.number().integer().required(i18next.t('generic.required')),
    maximumEnduranceTrainingPercentage: Yup.number().integer().required(i18next.t('generic.required')),
  });

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);

    try {
      const request: TelerehabilitationProgramRequest = {
        germanName: values.germanName,
        englishName: values.englishName,
        targetTrainingMinutes: values.targetTrainingMinutes,
        teConversion: values.teConversion,
        basicPackage: values.basicPackage,
        maximumBillableTherapyUnits: values.maximumBillableTherapyUnits,
        maximumBillableTeleTherapyUnits: values.maximumBillableTeleTherapyUnits,
        maximumEnduranceTrainingPercentage: values.maximumEnduranceTrainingPercentage,
      };
      
      if (data) {
        await authFetch(`/api/v1/telerehabilitation-program/${data.id}`, { method: 'PATCH', body: request });
      } else {
        await authFetch('/api/v1/telerehabilitation-program', { method: 'POST', body: request });
      }
      
      onSubmit();

    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, touched, errors, handleSubmit, handleChange, handleBlur, isSubmitting }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1 }}>
            <Stack gap={3}>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="germanName">
                    <Form.Label>
                      {i18next.t('telerehabilitationProgramModal.germanName')}
                    </Form.Label>
                    <Form.Control
                      name="germanName"
                      value={values.germanName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.germanName && !!errors.germanName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.germanName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="englishName">
                    <Form.Label>
                      {i18next.t('telerehabilitationProgramModal.englishName')}
                    </Form.Label>
                    <Form.Control
                      name="englishName"
                      value={values.englishName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.englishName && !!errors.englishName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.englishName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Form.Group controlId="targetTrainingMinutes">
                  <Form.Label>
                    {i18next.t('telerehabilitationProgramModal.targetTrainingMinutes')}
                  </Form.Label>
                  <Form.Control
                    name="targetTrainingMinutes"
                    type="number"
                    value={values.targetTrainingMinutes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.targetTrainingMinutes && !!errors.targetTrainingMinutes}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.targetTrainingMinutes}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row>
                <Form.Group controlId="maximumBillableTherapyUnits">
                  <Form.Label>
                    {i18next.t('telerehabilitationProgramModal.maximumBillableTherapyUnits')}
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="maximumBillableTherapyUnits"
                      type="number"
                      value={values.maximumBillableTherapyUnits}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.maximumBillableTherapyUnits && !!errors.maximumBillableTherapyUnits}
                    />
                    <InputGroup.Text>TE</InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.maximumBillableTherapyUnits}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Row>

              <Row>
                <Form.Group controlId="maximumBillableTeleTherapyUnits">
                  <Form.Label>
                    {i18next.t('telerehabilitationProgramModal.maximumBillableTeleTherapyUnits')}
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="maximumBillableTeleTherapyUnits"
                      type="number"
                      value={values.maximumBillableTeleTherapyUnits}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.maximumBillableTeleTherapyUnits && !!errors.maximumBillableTeleTherapyUnits}
                    />
                    <InputGroup.Text>TE</InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.maximumBillableTeleTherapyUnits}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Row>

              <Row>
                <Form.Group controlId="maximumEnduranceTrainingPercentage">
                  <Form.Label>
                    {i18next.t('telerehabilitationProgramModal.maximumEnduranceTrainingPercentage')}
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="maximumEnduranceTrainingPercentage"
                      type="number"
                      value={values.maximumEnduranceTrainingPercentage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.maximumEnduranceTrainingPercentage && !!errors.maximumEnduranceTrainingPercentage}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.maximumEnduranceTrainingPercentage}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Row>
              
              <Row>
                <Form.Group controlId="basicPackage">
                  <Form.Label>
                    {i18next.t('telerehabilitationProgramModal.basicPackage')}
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="basicPackage"
                      type="number"
                      value={values.basicPackage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.basicPackage && !!errors.basicPackage}
                    />
                    <InputGroup.Text>TE</InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.basicPackage}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Row>

              <Row>
                <Form.Group controlId="teConversion">
                  <Form.Label>
                    {i18next.t('telerehabilitationProgramModal.teConversion')}
                  </Form.Label>
                  <Form.Control
                    name="teConversion"
                    type="number"
                    value={values.teConversion}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.teConversion && !!errors.teConversion}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.teConversion}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.close')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.save')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TelerehabilitationProgramForm;
